import React, { useState, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StyleService from '@/services/Style.service';
import Style from '@/types/Style.type';
import { IconMaster, Messages, ColorMaster } from '@/common/constants';
import { useForm, SubmitHandler, Controller, FormProvider } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import Loading from '@/components/common/Loading';
import PageTitle from '@/components/common/PageTitle';
import { Box, Paper, FormControl, FormHelperText, InputLabel, MenuItem, Select, Grid, Icon } from '@mui/material';
import HookFormControls from '@/components/common/HookFormControls';
import CommonActionButton from '@/components/common/CommonActionButton';
import URL from '@/common/routes/Url';

const StatusDetail: React.FC = () => {
  const pathParams = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [color, setColor] = useState(ColorMaster[0]);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const useFormMethods = useForm<Style>({
    defaultValues: {
      name: '',
      nextDateFlag: false,
      icon: IconMaster[0],
      color: ColorMaster[0],
    },
    shouldUnregister: false,
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
  } = useFormMethods;

  const validationRules = {
    name: {
      required: '勤務形態名は必須項目です。',
      maxLength: { value: 8, message: '勤務形態名は8字以内で入力してください。' },
    },
    nextDateFlag: {},
    icon: { required: 'アイコンは必須項目です。' },
    color: {
      required: 'カラーは必須項目です。',
      pattern: {
        value: new RegExp('^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$'),
        message: 'カラーコードの形式が不正です。',
      },
    },
  };

  const menuItemIcons = IconMaster.map((iconName, index) => {
    return (
      <MenuItem key={index} value={iconName}>
        <Icon>{iconName}</Icon>
      </MenuItem>
    );
  });

  useLayoutEffect(() => {
    if (!pathParams.id) {
      setLoading(false);
      return;
    }
    StyleService.get(pathParams.id)
      .then((response: { data: Style }) => {
        reset(response.data);
        setColor(response.data.color);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        navigate(URL.styles);
        enqueueSnackbar(Messages.select.failure);
      });
  }, []);

  const onSubmit: SubmitHandler<Style> = async (data: Style) => {
    try {
      if (pathParams.id) {
        await StyleService.update(data, pathParams.id);
        enqueueSnackbar(Messages.update.success, { variant: 'info' });
      } else {
        await StyleService.create(data);
        enqueueSnackbar(Messages.create.success, { variant: 'info' });
      }
      navigate(URL.styles);
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message);
      return;
    }
  };

  const handleChange = (color: any) => {
    // Stateにカラーコードをセット
    setColor(color.hex);
    // HookFromにカラーコードをセット
    setValue('color', color.hex);
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container justifyContent="center" alignItems="center">
      <Grid item xs={12} md={8} lg={4}>
        <Box sx= {{ display: { xs: 'block', sm: 'none' } }}>
          <PageTitle>勤務形態マスタ ー 編集</PageTitle>
        </Box>
        <Paper variant="outlined" sx={{ my: { xs: 0, md: 0 }, p: { xs: 2, md: 3 } }}>
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container justifyContent="center" alignItems="center" spacing={3}>
                <Grid item xs={12} md={12}>
                  <HookFormControls
                    name="name"
                    type="text"
                    label="勤務形態名*"
                    rule={validationRules.name}
                    fullWidth
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={4} lg={4}>
                  <Controller
                    name="icon"
                    control={control}
                    rules={validationRules.icon}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth error={Boolean(fieldState.error)}>
                        <InputLabel id="icon-label">アイコン</InputLabel>
                        <Select style={{ textAlign: 'center', color: color }} labelId="icon-label" label="アイコン" {...field}>
                          {menuItemIcons}
                        </Select>
                        <FormHelperText>{fieldState.error?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={8} lg={8}>
                  <HookFormControls
                    type="color"
                    color={color}
                    onChange={handleChange}
                    rule={validationRules.color}
                    colorMaster={ColorMaster}
                  />
                </Grid>
                <Grid item xs={12}>
                  <HookFormControls
                    type="checkbox"
                    name="nextDateFlag"
                    label="次回出社日の必須チェック"
                    rule={validationRules.nextDateFlag}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <CommonActionButton isSubmitting={isSubmitting} />
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Paper>
      </Grid>
    </Grid>
  );
};
export default StatusDetail;
